import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
// import MainLayout from '../layouts/main';
import MainSimpleLayout from '../layouts/main-simple';
import DashboardLayout from '../layouts/dashboard';
import RootLayout from '../layouts/root';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// import PermissionBasedGuard from '../guards/PermissionBasedGuard';
// config
import {
  // METHOD_NAMES,
  PATH_AFTER_LOGIN,
  PATH_AFTER_LOGIN_AS_ROOT,
  PATH_CREATE_COMPANY,
  // PERMISSION_NAMES,
  ROLE_NAMES,
} from '../config';
import { PATH_DASHBOARD, PATH_ROOT } from './paths';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Auth routes
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Create Company Routes
    {
      path: 'company',
      children: [
        { element: <Navigate to={PATH_CREATE_COMPANY} replace />, index: true },
        {
          path: 'create',
          element: (
            <AuthGuard>
              <CreateCompany />
            </AuthGuard>
          ),
        },
      ],
    },

    // Root Routes
    {
      path: 'root',
      element: (
        <AuthGuard>
          <RoleBasedGuard accessibleRoles={[ROLE_NAMES.root]}>
            <RootLayout />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN_AS_ROOT} replace />, index: true },
        { path: 'app', element: <ComingSoon /> },
        {
          path: 'company',
          children: [
            { element: <Navigate to={PATH_ROOT.company.list} replace />, index: true },
            { path: 'list', element: <CompanyList /> },
          ],
        },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <RoleBasedGuard accessibleRoles={[ROLE_NAMES.root, ROLE_NAMES.admin, ROLE_NAMES.manager]}>
            <DashboardLayout />
          </RoleBasedGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },

        // ACTIVITY CONTROL
        {
          path: 'activities',
          element: (
            // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
            <GeneralActivities />
            // </PermissionBasedGuard>
          ),
        },
        {
          path: 'activity-list',
          children: [
            {
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <GeneralActivityList />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: ':id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <EventDetail parentPath={PATH_DASHBOARD.general.activityList} />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'alerts',
          children: [
            {
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.alert, METHOD_NAMES.get]]}>
                <GeneralAlert />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: ':id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.alert, METHOD_NAMES.get]]}>
                <AlertDetail />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'timekeepings',
          children: [
            {
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.alert, METHOD_NAMES.get]]}>
                <GeneralTimekeeping />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: ':id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.alert, METHOD_NAMES.get]]}>
                <TimekeepingDetail />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },

        // STATISTIC
        {
          path: 'company',
          element: (
            <RoleBasedGuard accessibleRoles={[ROLE_NAMES.root, ROLE_NAMES.admin]}>
              {/* <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.company, METHOD_NAMES.get]]}> */}
              <GeneralCompany />
              {/* </PermissionBasedGuard> */}
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events',
          children: [
            {
              element: (
                // <PermissionBasedGuard
                //   accessiblePermissions={[
                //     [PERMISSION_NAMES.report, METHOD_NAMES.get],
                //     [PERMISSION_NAMES.schedule, METHOD_NAMES.get],
                //   ]}
                // >
                <GeneralEvents />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: ':id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <EventDetail parentPath={PATH_DASHBOARD.events.root} />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },

        // USER
        {
          path: 'user',
          children: [
            { element: <Navigate to={PATH_DASHBOARD.user.list} replace />, index: true },

            // GROUP
            {
              path: 'group',
              children: [
                {
                  element: (
                    // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.group, METHOD_NAMES.get]]}>
                    <GroupList />
                    // </PermissionBasedGuard>
                  ),
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.group, METHOD_NAMES.get]]}>
                    <GroupList />
                    // </PermissionBasedGuard>
                  ),
                },
                {
                  path: 'create',
                  element: (
                    // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.group, METHOD_NAMES.post]]}>
                    <GroupCreate />
                    // </PermissionBasedGuard>
                  ),
                },
                {
                  path: 'edit/:groupId',
                  element: (
                    // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.group, METHOD_NAMES.put]]}>
                    <GroupCreate />
                    // </PermissionBasedGuard>
                  ),
                },
              ],
            },

            // ROLE
            {
              path: 'role',
              children: [
                {
                  element: (
                    // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.role, METHOD_NAMES.get]]}>
                    <RoleListFixed />
                    // </PermissionBasedGuard>
                  ),
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.role, METHOD_NAMES.get]]}>
                    <RoleListFixed />
                    // </PermissionBasedGuard>
                  ),
                },
                // {
                //   path: 'create',
                //   element: (
                //     // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.role, METHOD_NAMES.post]]}>
                //     <RoleCreate />
                //     // </PermissionBasedGuard>
                //   ),
                // },
                // {
                //   path: ':roleId/edit',
                //   element: (
                //     // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.role, METHOD_NAMES.put]]}>
                //     <RoleCreate />
                //     // </PermissionBasedGuard>
                //   ),
                // },
                // {
                //   path: ':roleId',
                //   element: (
                //     // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.role, METHOD_NAMES.get]]}>
                //     <RoleView />
                //     // </PermissionBasedGuard>
                //   ),
                // },
              ],
            },

            // USER
            {
              path: 'list',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.user, METHOD_NAMES.get]]}>
                <UserList />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: 'new',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.user, METHOD_NAMES.post]]}>
                <UserCreate />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: ':userId/edit',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.user, METHOD_NAMES.put]]}>
                <UserCreate />
                // </PermissionBasedGuard>
              ),
            },

            // OWN
            {
              path: 'account',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.all]]}>
                <UserAccount />
                // </PermissionBasedGuard>
              ),
            },
            // { path: 'profile', element: <UserProfile /> },
            // { path: 'cards', element: <UserCards /> },
          ],
        },

        // WORK
        {
          path: 'checklist',
          children: [
            {
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.checklist, METHOD_NAMES.get]]}>
                <CheckList />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: 'list',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.checklist, METHOD_NAMES.get]]}>
                <CheckList />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: 'create',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.checklist, METHOD_NAMES.post]]}>
                <CheckListCreate />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.checklist, METHOD_NAMES.put]]}>
                <CheckListCreate />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: ':id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.checklist, METHOD_NAMES.get]]}>
                <CheckListDetail />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'places',
          element: (
            // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.place, METHOD_NAMES.get]]}>
            <Place />
            // </PermissionBasedGuard>
          ),
        },
        {
          path: 'devices',
          children: [
            {
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.device, METHOD_NAMES.get]]}>
                <Device />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: 'create',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.device, METHOD_NAMES.post]]}>
                <DeviceCreate />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.device, METHOD_NAMES.put]]}>
                <DeviceCreate />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: ':id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.device, METHOD_NAMES.get]]}>
                <DeviceView />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'schedules',
          children: [
            {
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <Calendar />
                // </PermissionBasedGuard>
              ),
              index: true,
            },
            {
              path: 'list',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <ScheduleList />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: 'detail/:id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <ScheduleDetail />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: 'create',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.post]]}>
                <CalendarCreateEvent />
                // </PermissionBasedGuard>
              ),
            },
            {
              path: 'event/:id',
              element: (
                // <PermissionBasedGuard accessiblePermissions={[[PERMISSION_NAMES.schedule, METHOD_NAMES.get]]}>
                <EventDetail parentPath={PATH_DASHBOARD.schedules.root} />
                // </PermissionBasedGuard>
              ),
            },
          ],
        },

        // { path: 'ecommerce', element: <GeneralEcommerce /> },
        // { path: 'analytics', element: <GeneralAnalytics /> },
        // { path: 'banking', element: <GeneralBanking /> },
        // { path: 'booking', element: <GeneralBooking /> },
        // {
        //   path: 'e-commerce',
        //   children: [
        //     { element: <Navigate to="/dashboard/e-commerce/shop" replace />, index: true },
        //     { path: 'shop', element: <EcommerceShop /> },
        //     { path: 'product/:name', element: <EcommerceProductDetails /> },
        //     { path: 'list', element: <EcommerceProductList /> },
        //     { path: 'product/new', element: <EcommerceProductCreate /> },
        //     { path: 'product/:name/edit', element: <EcommerceProductCreate /> },
        //     { path: 'checkout', element: <EcommerceCheckout /> },
        //     { path: 'invoice', element: <EcommerceInvoice /> },
        //   ],
        // },
        // {
        //   path: 'mail',
        //   children: [
        //     { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
        //     { path: 'label/:customLabel', element: <Mail /> },
        //     { path: 'label/:customLabel/:mailId', element: <Mail /> },
        //     { path: ':systemLabel', element: <Mail /> },
        //     { path: ':systemLabel/:mailId', element: <Mail /> },
        //   ],
        // },
        // {
        //   path: 'chat',
        //   children: [
        //     { element: <Chat />, index: true },
        //     { path: 'new', element: <Chat /> },
        //     { path: ':conversationKey', element: <Chat /> },
        //   ],
        // },
        // { path: 'places', element: <Kanban /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        // { path: 'maintenance', element: <Maintenance /> },
        // { path: 'pricing', element: <Pricing /> },
        // { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainSimpleLayout />,
      children: [
        { element: <HomePage />, index: true },
        // { path: 'home', element: <Home /> },
        { path: 'contact-us', element: <ContactUs /> },
        // { path: 'privacy-policy', element: <PrivacyPolicy /> },
        // { path: 'terms', element: <TermOfService /> },
        { path: 'terms-and-privacy-policy', element: <TermAndPrivaryPolicy /> },
        // { path: 'about-us', element: <About /> },
        // { path: 'faqs', element: <Faqs /> },
      ],
    },
    // {
    //   path: '/',
    //   children: [{ element: <HomePage />, index: true }],
    // },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
// Dashboard

// General
const GeneralCompany = Loadable(lazy(() => import('../pages/dashboard/GeneralCompany')));
const GeneralEvents = Loadable(lazy(() => import('../pages/dashboard/GeneralEvents')));
const GeneralActivities = Loadable(lazy(() => import('../pages/dashboard/GeneralActivities')));
const GeneralActivityList = Loadable(lazy(() => import('../pages/dashboard/GeneralActivityList')));
const GeneralAlert = Loadable(lazy(() => import('../pages/dashboard/GeneralAlerts')));
const GeneralTimekeeping = Loadable(lazy(() => import('../pages/dashboard/GeneralTimekeepingList')));
// const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
// const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
// const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
// const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
// const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
// const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
// const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
// const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
// const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
// const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));

// const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
// const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
// const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
// const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
// const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));

const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
// const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
// const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// Main
const HomePage = Loadable(lazy(() => import('../pages/HomePage')));
// const Home = Loadable(lazy(() => import('../pages/Home')));
// const About = Loadable(lazy(() => import('../pages/About')));
// const Contact = Loadable(lazy(() => import('../pages/Contact')));
// const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
// const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
// const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
// const Payment = Loadable(lazy(() => import('../pages/Payment')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const ContactUs = Loadable(lazy(() => import('../pages/ContactUs')));
// const PrivacyPolicy = Loadable(lazy(() => import('../pages/PrivacyPolicy')));
// const TermOfService = Loadable(lazy(() => import('../pages/TermOfService')));
const TermAndPrivaryPolicy = Loadable(lazy(() => import('../pages/TermAndPrivaryPolicy')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// Company
const CreateCompany = Loadable(lazy(() => import('../pages/CreateCompany')));

// USER - GROUP
const GroupList = Loadable(lazy(() => import('../pages/dashboard/GroupList')));
const GroupCreate = Loadable(lazy(() => import('../pages/dashboard/GroupCreate')));

// USER - ROLE
const RoleListFixed = Loadable(lazy(() => import('../pages/dashboard/RoleListFixed')));
// const RoleList = Loadable(lazy(() => import('../pages/dashboard/RoleList')));
// const RoleCreate = Loadable(lazy(() => import('../pages/dashboard/RoleCreate')));
// const RoleView = Loadable(lazy(() => import('../pages/dashboard/RoleView')));

// CHECKLIST
const CheckList = Loadable(lazy(() => import('../pages/dashboard/CheckList')));
const CheckListCreate = Loadable(lazy(() => import('../pages/dashboard/CheckListCreate')));
const CheckListDetail = Loadable(lazy(() => import('../pages/dashboard/CheckListDetail')));

// PLACE
const Place = Loadable(lazy(() => import('../pages/dashboard/Place')));

// DEVICE
const Device = Loadable(lazy(() => import('../pages/dashboard/Device')));
const DeviceCreate = Loadable(lazy(() => import('../pages/dashboard/DeviceCreate')));
const DeviceView = Loadable(lazy(() => import('../pages/dashboard/DeviceView')));

// CALENDAR - EVENT
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const ScheduleList = Loadable(lazy(() => import('../pages/dashboard/ScheduleList')));
const ScheduleDetail = Loadable(lazy(() => import('../pages/dashboard/ScheduleDetail')));
const CalendarCreateEvent = Loadable(lazy(() => import('../pages/dashboard/CalendarCreateEvent')));
const EventDetail = Loadable(lazy(() => import('../pages/dashboard/EventDetail')));

// ALERT
const AlertDetail = Loadable(lazy(() => import('../pages/dashboard/AlertDetail')));

// TIMEKEEPING
const TimekeepingDetail = Loadable(lazy(() => import('../pages/dashboard/TimekeepingDetail')));

// Root
const CompanyList = Loadable(lazy(() => import('../pages/root/CompanyList')));
